import { useEffect, useState } from "react";
import {
    Zap, Globe, Target, QrCode, CreditCard, Bitcoin,
    ArrowUp, ArrowDown, Settings, Power, Plus, ArrowUpDown,
} from "lucide-react";
import Loading from "../../components/UI/Loading/Loading";
import { authService } from "../../services/api";
import "./index.css";
import { useNotification } from "../../components/Notifications/NotificationContext";
import AlertBox from "../../components/UI/AlertBox";
import { useAuth } from "../../context/AuthProvider.jsx";
import { getVisibleBots } from "../../utils/bots";
import { ScopePicker, AcceptedCurrencies, MoreCurrencies, STRIPE_CURRENCIES, BetaBadge } from "./shared";
import CooudConfigPanel from "./CooudConfigPanel";
import CardCredentialsPanel, { isFormConnected } from "./CardCredentialsPanel";

// Gateways whitelabel da mesma API: usam par de chaves pública/secreta.
// Mantenha em sincronia com WHITELABEL_GATEWAYS no backend (gateways.py).
const WHITELABEL_GATEWAYS = ["omegapay", "oasyfy"];

// Filtros da grade "Gateways Disponíveis".
const AVAIL_FILTERS = [
    { id: "all", label: "Todos", Icon: null },
    { id: "pix", label: "PIX", Icon: QrCode },
    { id: "card", label: "Cartão", Icon: CreditCard },
    { id: "crypto", label: "Crypto", Icon: Bitcoin },
];

/**
 * Painel do Stripe (cartão). Diferente dos gateways PIX, aqui não há campo de
 * token: o vendedor CONECTA a própria conta via OAuth. Quando conectado, mostra
 * a conta (acct_...), o seletor de moedas e Reconectar/Desconectar.
 */
// Igual a MAX_ACCEPTED_CURRENCIES do backend (menu de moeda do bot).
const MAX_STRIPE_CURRENCIES = 40;

export function StripeConfigPanel({ item, bots, onDone }) {
    const { showError, showSuccess } = useNotification();
    const creds = item.credentials || {};
    const connected = Boolean(creds.stripe_account_id);
    // BRL é sempre aceito (não pode ser desmarcado); o vendedor só adiciona as gringas.
    const [currencies, setCurrencies] = useState(
        ["BRL", ...(creds.currencies?.filter((c) => c !== "BRL") || [])]
    );
    const [scope, setScope] = useState(item.upg?.scope === 'specific' ? 'specific' : 'global');
    const [botIds, setBotIds] = useState(item.upg?.scoped_bot_ids || []);
    const [busy, setBusy] = useState(false);

    // Catálogo da Stripe vindo do backend (130 moedas da conta BR); as 4
    // principais ficam nas bandeiras grandes, o resto em "Mais moedas".
    const extraOptions = (item.gateway.currency_options || []).filter((c) => !STRIPE_CURRENCIES.includes(c));
    const toggleCurrency = (c) => {
        if (c === "BRL") return; // BRL é fixo
        setCurrencies((prev) => {
            if (prev.includes(c)) return prev.filter((x) => x !== c);
            if (prev.length >= MAX_STRIPE_CURRENCIES) return prev;
            return [...prev, c];
        });
    };

    const toggleBot = (id) =>
        setBotIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

    const connect = async () => {
        try {
            setBusy(true);
            const res = await authService.gateways.stripeConnect();
            const url = res.data?.url;
            if (url) window.location.href = url; // redireciona pro consentimento do Stripe
            else showError(res.data?.error || "Stripe não configurado no servidor.");
        } catch (err) {
            showError(err?.response?.data?.error || "Erro ao iniciar conexão com o Stripe.");
        } finally {
            setBusy(false);
        }
    };

    const disconnect = async () => {
        if (!window.confirm("Desconectar sua conta Stripe? Os pagamentos por cartão param até reconectar.")) return;
        try {
            setBusy(true);
            await authService.gateways.stripeDisconnect();
            showSuccess("Conta Stripe desconectada.");
            onDone?.();
        } catch (err) {
            showError(err?.response?.data?.error || "Erro ao desconectar.");
        } finally {
            setBusy(false);
        }
    };

    // Salva moedas + escopo (Global ou Específico por bot) numa tacada só.
    const save = async () => {
        if (!currencies.length) { showError("Selecione ao menos uma moeda."); return; }
        if (scope === 'specific' && botIds.length === 0) { showError("Selecione ao menos um bot."); return; }
        try {
            setBusy(true);
            await authService.gateways.stripeSetCurrencies(currencies);
            await authService.gateways.saveScope(item.gateway.name, scope, scope === 'specific' ? botIds : []);
            showSuccess("Configuração salva.");
            onDone?.();
        } catch (err) {
            showError(err?.response?.data?.error || "Erro ao salvar.");
        } finally {
            setBusy(false);
        }
    };

    if (!connected) {
        return (
            <div className="spt-form">
                <p className="text-gray-400" style={{ fontSize: 13 }}>
                    Conecte sua conta Stripe para receber pagamentos via cartão internacional. A
                    cobrança é feita na sua própria conta; a Spectrum cobra a comissão automaticamente.
                </p>
                <div className="spt-form-actions">
                    <button type="button" className="btn-secondary" onClick={() => onDone?.()}>Cancelar</button>
                    <button type="button" className="btn-primary" onClick={connect} disabled={busy}>
                        {busy ? "Abrindo..." : "Conectar com Stripe"}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-w-0 w-full flex flex-col gap-4 overflow-y-auto">
            <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
                {/* Conta conectada */}
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-2">
                    <div className="flex items-center gap-2">
                        <i className="fas fa-check-circle text-white/50" />
                        <p className="text-sm font-medium text-emerald-400">Stripe Conectado</p>
                    </div>
                    <p className="text-xs text-white/50">
                        Conta conectada:{" "}
                        <code className="px-1 py-0.5 bg-white/[0.02] rounded text-xs text-white/60">{creds.stripe_account_id}</code>
                        {creds.livemode === false && <span className="text-white/40"> (modo teste)</span>}
                    </p>
                    <p className="text-xs text-white/50">Sua conta esta conectada para pagamentos via cartao internacional.</p>
                </div>

                {/* Moedas aceitas */}
                <div className="space-y-2">
                    <label className="flex items-center gap-2 text-xs leading-none font-medium select-none text-white/40">
                        Moedas aceitas
                    </label>
                    <AcceptedCurrencies value={currencies} onToggle={toggleCurrency} />
                    {extraOptions.length > 0 && (
                        <details className="rounded-xl border border-white/[0.06] p-3" open={currencies.some((c) => extraOptions.includes(c))}>
                            <summary className="cursor-pointer text-xs text-white/60 select-none">
                                Mais moedas ({currencies.filter((c) => extraOptions.includes(c)).length} selecionadas)
                            </summary>
                            <div className="mt-3">
                                <MoreCurrencies options={extraOptions} value={currencies}
                                    onToggle={toggleCurrency} max={MAX_STRIPE_CURRENCIES} />
                            </div>
                        </details>
                    )}
                    <p className="text-[11px] text-white/40">Em fluxos globais, o cliente escolhe entre as moedas selecionadas.</p>
                </div>

                {/* Reconectar / Desconectar */}
                <div className="flex gap-2 pt-2">
                    <button type="button" onClick={connect} disabled={busy}
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all cursor-pointer disabled:opacity-50 border hover:border-white/10 h-10 px-4 py-2 flex-1 bg-white/[0.04] border-white/[0.06] text-white/50 hover:text-white hover:bg-white/[0.06]">
                        <i className="fas fa-external-link-alt mr-2" />Reconectar conta
                    </button>
                    <button type="button" onClick={disconnect} disabled={busy}
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all cursor-pointer disabled:opacity-50 shrink-0 border hover:border-white/10 h-10 px-4 py-2 bg-white/[0.04] border-red-500/30 text-red-400 hover:text-red-300 hover:bg-red-500/10">
                        <i className="fas fa-trash mr-2" />Desconectar
                    </button>
                </div>

                {/* Info split */}
                <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-1">
                    <p className="text-xs text-white/50">
                        <span className="text-[var(--brand-400)] font-medium">Split automatico:</span> A plataforma cobra comissao automaticamente via{" "}
                        <code className="px-1 py-0.5 bg-white/[0.02] rounded text-xs">application_fee</code>.
                    </p>
                    <p className="text-xs text-white/50">
                        <span className="text-[var(--brand-400)] font-medium">Aceita cartoes internacionais</span> (Visa, Mastercard, Amex, etc).
                    </p>
                </div>

                {/* Escopo do gateway */}
                <ScopePicker scope={scope} setScope={setScope} bots={bots} botIds={botIds} toggleBot={toggleBot} />
            </div>

            {/* Ações */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 pt-3 shrink-0 border-t border-white/[0.06]">
                <button type="button" onClick={() => onDone?.()}
                    className="btn-secondary">
                    Cancelar
                </button>
                <button type="button" onClick={save} disabled={busy}
                    className="btn-primary">
                    {busy ? "Salvando..." : "Salvar"}
                </button>
            </div>
        </div>
    );
}

// Form de gateway PIX: credenciais + escopo (Global/Específico) num único Salvar.
function GatewayTokenForm({ item, bots, saving, onCancel, onSubmit }) {
    const displayName = item.gateway.display_name;
    const hasScope = !!item.upg?.id;
    const [scope, setScope] = useState(item.upg?.scope === 'specific' ? 'specific' : 'global');
    const [botIds, setBotIds] = useState(item.upg?.scoped_bot_ids || []);

    const toggleBot = (id) => setBotIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

    const submit = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const tokenData = {};
        if (displayName === "syncpay") {
            tokenData.client_id = formData.get('client_id');
            tokenData.client_secret = formData.get('client_secret');
        } else if (WHITELABEL_GATEWAYS.includes(displayName)) {
            tokenData.public_key = formData.get('public_key');
            tokenData.secret_key = formData.get('secret_key');
        } else {
            tokenData.token = formData.get('token');
            tokenData.token_type = 'api_key';
        }
        onSubmit(item.gateway.name, tokenData, hasScope ? { scope, botIds } : null);
    };

    return (
        <form className="spt-form" onSubmit={submit}>
            {displayName === "syncpay" ? (
                <>
                    <input name="client_id" autoComplete="off" defaultValue=""
                        placeholder={item.credentials?.client_id ? "Client ID (preencha p/ alterar)" : "Client ID"} className="input2" />
                    <input name="client_secret" type="password" autoComplete="new-password" defaultValue=""
                        placeholder={item.credentials?.client_secret ? "Client Secret (preencha p/ alterar)" : "Client Secret"} className="input2" />
                </>
            ) : WHITELABEL_GATEWAYS.includes(displayName) ? (
                <>
                    <input name="public_key" autoComplete="off" defaultValue=""
                        placeholder={item.credentials?.public_key ? "Chave pública (preencha p/ alterar)" : "Chave pública (x-public-key)"} className="input2" />
                    <input name="secret_key" type="password" autoComplete="new-password" defaultValue=""
                        placeholder={item.credentials?.secret_key ? "Chave secreta (preencha p/ alterar)" : "Chave secreta (x-secret-key)"} className="input2" />
                </>
            ) : (
                <>
                    <input type="hidden" name="token_type" value="api_key" />
                    <input name="token" type="password" autoComplete="new-password" defaultValue=""
                        placeholder={item.credentials?.api_key ? "Chave / token da API (preencha p/ alterar)" : "Chave / token da API"} className="input2" />
                </>
            )}

            {hasScope && (
                <div className="mt-3 w-full">
                    <ScopePicker scope={scope} setScope={setScope} bots={bots} botIds={botIds} toggleBot={toggleBot} />
                </div>
            )}

            <div className="spt-form-actions">
                <button type="button" className="btn-secondary" onClick={onCancel}>Cancelar</button>
                <button className="btn-primary" type="submit" disabled={saving}>
                    {saving ? "Salvando..." : "Salvar"}
                </button>
            </div>
        </form>
    );
}

export default function Gateways({ items } = {}) {
    const { showError, showSuccess, showInfo } = useNotification();
    const { user } = useAuth();
    const bots = getVisibleBots(user?.bots);
    const [gateways, setGateways] = useState([]);
    const [loading, setLoading] = useState(true);
    // const [error, setError] = useState(null);
    const [savingToken, setSavingToken] = useState(null);

    const [activeGateway, setActiveGateway] = useState(null);
    const [availFilter, setAvailFilter] = useState("all");

    // Fetch gateways from API
    useEffect(() => {
        const fetchGateways = async () => {
            try {
                setLoading(true);
                const res = await authService.gateways.list_gateways();
                const json = res.data;

                if (!json.success) {
                    // setError(json.error || "Erro ao carregar gateways");
                    showError(json.error || "Erro ao carregar gateways");
                    return;
                }

                // Transform API response to match component structure
                const transformedItems = json.data.map(item => ({
                    gateway: item.gateway,
                    upg: item.user_gateway,
                    credentials: item.user_gateway?.credentials || {},
                    showToken: true
                }));

                // Sort by priority
                const sorted = transformedItems.sort((a, b) => {
                    const pa = a.upg?.priority ?? 9999;
                    const pb = b.upg?.priority ?? 9999;
                    return pa - pb;
                });

                setGateways(sorted);
            } catch (err) {
                // setError("Erro ao carregar gateways");
                showError("Erro ao carregar gateways");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchGateways();
    }, []);

    // Feedback do retorno do OAuth do Stripe (?stripe=connected|error|invalid).
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const s = params.get("stripe");
        if (!s) return;
        if (s === "connected") showSuccess("Stripe conectado com sucesso!");
        else if (s === "invalid") showError("Link de conexão inválido ou expirado. Tente de novo.");
        else showError("Falha ao conectar o Stripe. Tente novamente.");
        params.delete("stripe");
        const qs = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Cooud pendente (convite / verificação / cobranças bloqueadas): ao abrir a
    // tela, reconsulta a Cooud uma vez e recarrega a lista com o estado real.
    const [cooudChecked, setCooudChecked] = useState(false);
    useEffect(() => {
        if (cooudChecked || !gateways.length) return;
        const it = gateways.find((g) => g.gateway.name === "cooud");
        if (!it || !cooudStatus(it)) return;
        setCooudChecked(true);
        authService.gateways.cooudConfig()
            .then(() => refreshGateways({ keepOpen: true }))
            .catch(() => {});
    }, [gateways]); // eslint-disable-line react-hooks/exhaustive-deps

    // Retorno da verificação hospedada da Cooud (?cooud=return|error|invalid).
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const c = params.get("cooud");
        if (!c) return;
        if (c === "return") showInfo("Verificação enviada à Cooud. O status pode levar alguns instantes para atualizar.");
        else if (c === "invalid") showError("Link de verificação inválido ou expirado. Tente de novo.");
        else showError("Não foi possível abrir a verificação da Cooud. Tente novamente.");
        params.delete("cooud");
        const qs = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Recarrega a lista de gateways e fecha o painel aberto (usado após ações Stripe).
    const refreshGateways = async ({ keepOpen = false } = {}) => {
        try {
            const res = await authService.gateways.list_gateways();
            const json = res.data;
            if (json.success) {
                const transformed = json.data.map((it) => ({
                    gateway: it.gateway,
                    upg: it.user_gateway,
                    credentials: it.user_gateway?.credentials || {},
                    showToken: true,
                }));
                transformed.sort((a, b) => (a.upg?.priority ?? 9999) - (b.upg?.priority ?? 9999));
                setGateways(transformed);
            }
        } catch (err) {
            console.error(err);
        }
        if (!keepOpen) setActiveGateway(null);
    };

    async function saveToken(gatewayName, credentials, scopeData = null) {
        try {
            setSavingToken(gatewayName);
            showInfo("Validando credenciais... geramos um PIX de teste para conferir.");
            await authService.gateways.saveToken(gatewayName, credentials);
            if (scopeData) {
                if (scopeData.scope === 'specific' && scopeData.botIds.length === 0) {
                    showError('Selecione ao menos um bot.');
                    setSavingToken(null);
                    return;
                }
                await authService.gateways.saveScope(gatewayName, scopeData.scope, scopeData.scope === 'specific' ? scopeData.botIds : []);
            }

            // Refresh gateways list
            const res = await authService.gateways.list_gateways();
            const json = res.data;

            if (json.success) {
                const transformedItems = json.data.map(item => ({
                    gateway: item.gateway,
                    upg: item.user_gateway,
                    credentials: item.user_gateway?.credentials || {},
                    showToken: true
                }));

                const sorted = transformedItems.sort((a, b) => {
                    const pa = a.upg?.priority ?? 9999;
                    const pb = b.upg?.priority ?? 9999;
                    return pa - pb;
                });

                setGateways(sorted);

                showSuccess("Credenciais validadas e salvas com sucesso");
                setSavingToken(null);
                setActiveGateway(null);
            }
        } catch (err) {
            // O backend devolve a razão exata (credenciais inválidas, split recusado, etc.)
            showError(err?.response?.data?.error || "Erro ao salvar token");
            console.error(err);
        } finally {
            setSavingToken(null);
        }
    }

    // ── Classificação: PIX x Cartão, Ativo x Inativo ────────────────────────
    const isCardItem = (it) => it.gateway.method === 'card' || it.gateway.name === 'stripe';
    // Marca por formulário (Eagle...): conectada só com todos os campos; após
    // desconectar os campos não-secretos ficam salvos e não podem contar.
    const hasCreds = (it) => it.gateway.credential_fields?.length ? isFormConnected(it) : !!(
        it.credentials?.api_key || it.credentials?.client_id ||
        it.credentials?.public_key || it.credentials?.stripe_account_id ||
        it.credentials?.cooud_account_id || it.credentials?.invite_sent
    );
    // Ativo = tem credenciais E não foi desativado (upg.active !== false).
    const isActiveItem = (it) => hasCreds(it) && it.upg?.active !== false;

    // Reordena na hora (↑/↓) DENTRO da fila do método (PIX ou Cartão):
    // recalcula as prioridades 1..n da fila para o sort refletir a mudança.
    // Persiste só ao clicar "Salvar ordem".
    function moveActive(item, dir) {
        const queue = isCardItem(item) ? cardActive : pixActive;
        const order = queue.map((g) => g.gateway.name);
        const idx = order.indexOf(item.gateway.name);
        const target = idx + dir;
        if (target < 0 || target >= order.length) return;
        [order[idx], order[target]] = [order[target], order[idx]];
        const priorityByName = {};
        order.forEach((name, i) => { priorityByName[name] = i + 1; });
        setGateways(gateways.map((g) => {
            const p = priorityByName[g.gateway.name];
            if (p == null || !g.upg) return g;
            return { ...g, upg: { ...g.upg, priority: p } };
        }));
    }

    async function saveOrder() {
        // Duas filas independentes, na ordem exibida: PIX (o 1º é o padrão) e
        // Cartão (o 1º é o cartão principal; os demais, fallback).
        const ids = pixActive.map((g) => g.upg?.id).filter(Boolean);
        const cardIds = cardActive.map((g) => g.upg?.id).filter(Boolean);

        try {
            const res = await authService.gateways.reorder(ids, cardIds);
            const json = res.data;

            if (json.error) {
                // setError(json.error);
                showError(json.error);
            } else {
                // setError(null);
                // alert(json.message || "Ordem salva com sucesso");
                showSuccess(json.message || "Ordem salva com sucesso");
                // Refresh gateways
                const listRes = await authService.gateways.list_gateways();
                const data = listRes.data;
                if (data.success) {
                    const transformedItems = data.data.map(item => ({
                        gateway: item.gateway,
                        upg: item.user_gateway,
                        credentials: item.user_gateway?.credentials || {},
                        showToken: true
                    }));
                    const sorted = transformedItems.sort((a, b) => {
                        const pa = a.upg?.priority ?? 9999;
                        const pb = b.upg?.priority ?? 9999;
                        return pa - pb;
                    });
                    setGateways(sorted);
                }
            }
        } catch (err) {
            showError("Erro ao salvar ordem");
            // setError("Erro ao salvar ordem");
            console.error(err);
        }
    }

    const activeList = gateways
        .filter((it) => isActiveItem(it))
        .sort((a, b) => (a.upg?.priority ?? 9999) - (b.upg?.priority ?? 9999));
    const pixActive = activeList.filter((it) => !isCardItem(it));
    const cardActive = activeList.filter((it) => isCardItem(it));

    // Estado da conta Cooud (pendências que impedem ou limitam a cobrança).
    const cooudStatus = (it) => {
        if (it.gateway.name !== "cooud") return null;
        const c = it.credentials || {};
        if (!c.cooud_account_id) return c.invite_sent ? { label: "Convite pendente", blocking: true } : null;
        if (c.charges_enabled === false) return { label: "Cobranças bloqueadas", blocking: true };
        if (c.payouts_enabled === false || (c.requirements_due || []).length > 0)
            return { label: "Verificação pendente", blocking: false };
        return null;
    };
    const availFiltered = gateways
        .filter((it) => !isActiveItem(it))
        .filter((it) => availFilter === "all" || (it.gateway.method || "pix") === availFilter);
    const activeItem = gateways.find((g) => g.gateway.name === activeGateway) || null;

    const gwLogo = (displayName) => {
        try { return require(`../../static/images/gateways/${displayName}.webp`); }
        catch { return require(`../../static/images/gateways/wiinpay.webp`); }
    };

    const methodMeta = (m) => {
        if (m === "card") return { label: "Cartão", cls: "bg-indigo-500/12 text-indigo-300", Icon: CreditCard };
        if (m === "crypto") return { label: "Crypto", cls: "bg-amber-500/12 text-amber-300", Icon: Bitcoin };
        return { label: "PIX", cls: "bg-sky-500/12 text-sky-300", Icon: QrCode };
    };

    const MethodPill = ({ method }) => {
        const meta = methodMeta(method || "pix");
        const Icon = meta.Icon;
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ${meta.cls}`}>
                <Icon size={11} /> {meta.label}
            </span>
        );
    };

    const ScopePill = ({ item }) => (
        item.upg?.scope === "specific" ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-sky-500/15 text-sky-300">
                <Target size={11} /> Bots específicos
            </span>
        ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                <Globe size={11} /> Todos os fluxos
            </span>
        )
    );

    async function deactivateGateway(item) {
        const card = isCardItem(item);
        const name = item.gateway.display_name || item.gateway.name;
        const keepsCreds = !item.gateway.credential_fields?.length;
        const detail = keepsCreds ? "as credenciais são mantidas" : "as chaves secretas são apagadas";
        if (!window.confirm(`Desativar ${name}? Ele sai da fila de pagamento (${detail}).`)) return;
        try {
            if (item.gateway.name === "cooud") await authService.gateways.cooudDisconnect();
            else if (item.gateway.credential_fields?.length) await authService.gateways.disconnectCard(item.gateway.name);
            else if (card) await authService.gateways.stripeDisconnect();
            else await authService.gateways.deactivate(item.gateway.name);
            showSuccess("Gateway desativado.");
            refreshGateways();
        } catch (err) {
            showError(err?.response?.data?.error || "Erro ao desativar gateway.");
        }
    }

    // Card de gateway ATIVO (grid): enumerador + papel + setas ↑/↓ + ações.
    // A posição é DENTRO da fila do método (PIX ou Cartão).
    const renderActiveCard = (item, index, queue) => {
        const card = isCardItem(item);
        const isPrincipal = index === 0;
        const role = isPrincipal ? "Principal" : `Fallback ${index}`;
        const status = cooudStatus(item);
        // Só o PIX padrão é travado: cartão pode ser desconectado a qualquer hora.
        const lockDeactivate = isPrincipal && !card;
        return (
            <div key={item.gateway.name}
                className="relative rounded-xl border border-white/[0.07] bg-white/[0.015] p-4 flex flex-col transition hover:border-white/[0.14]">
                <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-md text-white text-xs font-bold flex items-center justify-center ${isPrincipal ? "bg-emerald-500" : "bg-sky-500"}`}>{index + 1}</span>
                        <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${isPrincipal ? "bg-emerald-500/15 text-emerald-400" : "bg-sky-500/15 text-sky-300"}`}>{role}</span>
                    </div>
                    {queue.length > 1 && (
                        <div className="flex gap-1">
                            <button type="button" className="reorder-btn" aria-label="Subir prioridade" disabled={index === 0}
                                onClick={() => moveActive(item, -1)}><ArrowUp size={15} /></button>
                            <button type="button" className="reorder-btn" aria-label="Descer prioridade" disabled={index === queue.length - 1}
                                onClick={() => moveActive(item, 1)}><ArrowDown size={15} /></button>
                        </div>
                    )}
                </div>

                <div className="flex flex-col items-center text-center gap-2 py-3">
                    <img src={gwLogo(item.gateway.display_name)} alt={item.gateway.name}
                        className="rounded-xl border border-white/10 object-contain" style={{ width: 48, height: 48 }}
                        onError={(e) => (e.currentTarget.src = gwLogo("wiinpay"))} />
                    <div className="flex items-center gap-2 flex-wrap justify-center">
                        <strong className="text-white text-[15px]">{item.gateway.display_name || item.gateway.name}</strong>
                        <MethodPill method={item.gateway.method} />
                        <BetaBadge name={item.gateway.name} />
                    </div>
                    <ScopePill item={item} />
                    {status && (
                        <span title={status.blocking ? "Não é usado para cobrar até resolver" : "Cobra normalmente; repasses retidos até concluir"}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ${status.blocking ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`}>
                            {status.label}{status.blocking ? " · não cobra" : ""}
                        </span>
                    )}
                </div>

                <div className="mt-2 pt-3 border-t border-white/[0.06] flex items-center justify-center gap-0 text-xs">
                    <button type="button" onClick={() => setActiveGateway(item.gateway.name)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-transparent text-white/70 hover:text-white hover:border-white/[0.08] hover:bg-white/[0.08] transition">
                        <Settings size={14} /> Config
                    </button>
                    <button type="button" onClick={() => deactivateGateway(item)} disabled={lockDeactivate}
                        title={lockDeactivate ? "O gateway padrão não pode ser desativado" : "Desativar"}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-transparent transition ${lockDeactivate
                            ? "text-white/25 cursor-not-allowed"
                            : "text-red-400 hover:text-red-300 hover:border-red-500/20 hover:bg-red-500/10"}`}>
                        <Power size={14} /> Desativar
                    </button>
                </div>
            </div>
        );
    };

    // Card de gateway DISPONÍVEL (grid): logo + método + "+" para configurar.
    const renderAvailableCard = (item) => {
        const desc = isCardItem(item)
            ? "Pagamento via Cartão"
            : item.gateway.method === "crypto" ? "Pagamento via Cripto" : "Pagamento via PIX";
        return (
            <div key={item.gateway.name} role="button" tabIndex={0}
                onClick={() => setActiveGateway(item.gateway.name)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setActiveGateway(item.gateway.name); }}
                aria-label={`Adicionar ${item.gateway.display_name || item.gateway.name}`}
                className="group relative rounded-xl border border-white/[0.07] bg-white/[0.015] p-4 cursor-pointer transition hover:border-white/[0.18] hover:bg-white/[0.03]">
                <Plus size={16} className="absolute top-3 right-3 text-white/30 group-hover:text-white/70 transition" />
                <div className="flex flex-col items-center text-center gap-2">
                    <img src={gwLogo(item.gateway.display_name)} alt={item.gateway.name}
                        className="rounded-xl border border-white/10 object-contain" style={{ width: 48, height: 48 }}
                        onError={(e) => (e.currentTarget.src = gwLogo("wiinpay"))} />
                    <div className="flex items-center gap-2 flex-wrap justify-center">
                        <strong className="text-white text-sm">{item.gateway.display_name || item.gateway.name}</strong>
                        <MethodPill method={item.gateway.method} />
                        <BetaBadge name={item.gateway.name} />
                    </div>
                    <span className="text-xs text-white/40">{desc}</span>
                </div>
            </div>
        );
    };

    return (
        <div className="page">
            <header style={{ marginBottom: 24 }}>
                <h1 className='title'>Gateways de Pagamento</h1>
                <p className='text-gray-400'>
                    Configure suas chaves, defina o gateway padrão e ordene a cadeia de fallback.
                </p>
            </header>

            {loading ? (
                <Loading text="Carregando gateways..." />
            ) : (
                <>
                    {/* ── Gateways Ativos ── */}
                    <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 md:p-5 mb-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Zap size={18} className="text-[var(--brand-400)]" />
                            <h2 className="text-base font-semibold text-white">Gateways Ativos</h2>
                            <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-white/[0.06] text-white/60">{activeList.length}</span>
                        </div>
                        {activeList.length === 0 ? (
                            <p className="text-sm text-white/40">Nenhum gateway ativo. Adicione um da lista de disponíveis abaixo.</p>
                        ) : (
                            <>
                                {[["PIX", pixActive, QrCode], ["Cartão", cardActive, CreditCard]].map(([label, queue, Icon]) => (
                                    queue.length > 0 && (
                                        <div key={label} className="mb-4 last:mb-0">
                                            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white/40 mb-2">
                                                <Icon size={13} /> Fila {label}
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                                {queue.map((item, i) => renderActiveCard(item, i, queue))}
                                            </div>
                                        </div>
                                    )
                                ))}
                                {(pixActive.length > 1 || cardActive.length > 1) && (
                                    <div className="flex justify-end mt-4">
                                        <button className="btn-primary flex items-center gap-2" onClick={saveOrder}>
                                            <ArrowUpDown size={15} /> Salvar ordem
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </section>

                    {/* ── Gateways Disponíveis ── */}
                    <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 md:p-5 mb-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Globe size={18} className="text-white/50" />
                            <h2 className="text-base font-semibold text-white">Gateways Disponíveis</h2>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {AVAIL_FILTERS.map((f) => (
                                <button key={f.id} type="button" onClick={() => setAvailFilter(f.id)}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition ${availFilter === f.id
                                        ? "bg-[rgba(139,92,246,0.18)] border-[rgba(139,92,246,0.4)] text-white"
                                        : "bg-white/[0.03] border-white/[0.08] text-white/60 hover:border-white/20"}`}>
                                    {f.Icon && <f.Icon size={13} />} {f.label}
                                </button>
                            ))}
                        </div>
                        {availFiltered.length === 0 ? (
                            <p className="text-sm text-white/40">Nenhum gateway nesse filtro.</p>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                                {availFiltered.map(renderAvailableCard)}
                            </div>
                        )}
                    </section>

                    <AlertBox type="info">
                        <p>Os gateways PIX seguem a ordem de prioridade configurada. Se o principal estiver em manutenção ou instável, a Spectrum alterna automaticamente para o próximo da fila, mantendo a operação estável e sem perda de pagamentos.</p>
                    </AlertBox>

                    {/* ── Modal de configuração ── */}
                    {activeItem && (
                        <div className="gw-modal-overlay" onClick={() => setActiveGateway(null)}>
                            <div className="gw-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="gw-modal-head">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <img
                                            src={require(`../../static/images/gateways/${activeItem.gateway.display_name}.webp`)}
                                            alt={activeItem.gateway.name}
                                            className="logo-rounded"
                                            onError={(e) => (e.currentTarget.src = "/static/images/gateways/wiinpay.webp")}
                                        />
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <strong className="spt-name">{activeItem.gateway.display_name || activeItem.gateway.name}</strong>
                                                <BetaBadge name={activeItem.gateway.name} />
                                            </div>
                                            <div className="text-xs text-white/40">
                                                {isCardItem(activeItem) ? 'Cartão' : 'PIX'} · {isActiveItem(activeItem) ? 'Ativo' : 'Inativo'}
                                            </div>
                                        </div>
                                    </div>
                                    <button type="button" className="gw-modal-close" onClick={() => setActiveGateway(null)} aria-label="Fechar">
                                        <i className="fas fa-times" />
                                    </button>
                                </div>

                                <div className="gw-modal-body">
                                    {activeItem.gateway.name === "cooud" ? (
                                        <CooudConfigPanel item={activeItem} bots={bots} onDone={refreshGateways} />
                                    ) : activeItem.gateway.credential_fields?.length ? (
                                        <CardCredentialsPanel item={activeItem} bots={bots} onDone={refreshGateways} />
                                    ) : isCardItem(activeItem) ? (
                                        <StripeConfigPanel item={activeItem} bots={bots} onDone={refreshGateways} />
                                    ) : (
                                        <GatewayTokenForm
                                            item={activeItem}
                                            bots={bots}
                                            saving={savingToken === activeItem.gateway.name}
                                            onCancel={() => setActiveGateway(null)}
                                            onSubmit={saveToken}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )
            }
        </div >
    );
}
